import axios from '@api';

import type {
  DashboardChartResponse,
  DashboardFilterOption,
  DashboardFiltersPayload,
  DashboardFiltersResponse,
  DashboardTablePayload,
  DashboardTableResponse,
} from './types';
import { transformFilters, transformTableData } from './utilities/helpers';

interface DemographicsGlobalFilters {
  demographic_attribute?: DashboardFilterOption;
  product_level_one?: DashboardFilterOption;
  time_int?: DashboardFilterOption;
}

interface ReadStoresChartOptions {
  chartsFilters?: StoresChartsFilters;
  globalFilters?: StoresGlobalFilters;
  signal?: AbortSignal;
}

interface ReadSummaryChartOptions {
  chartsFilters?: SummaryChartsFilters;
  globalFilters?: SummaryGlobalFilters;
  signal?: AbortSignal;
}

interface ReadSummaryFiltersOptions {
  globalFilters?: SummaryGlobalFilters;
  signal?: AbortSignal;
}

interface StoresChartsFilters {
  metric?: DashboardFilterOption;
  product_level_three?: DashboardFilterOption[];
  store_level_one?: DashboardFilterOption;
  store_level_three?: DashboardFilterOption;
  store_level_two?: DashboardFilterOption;
}

interface StoresGlobalFilters {
  product_level_one?: DashboardFilterOption;
  time_int?: DashboardFilterOption;
}

interface SummaryChartsFilters {
  metric?: DashboardFilterOption;
  product?: DashboardFilterOption[];
}

interface SummaryGlobalFilters {
  breakout?: DashboardFilterOption;
  product_level_one?: DashboardFilterOption;
  time_int?: DashboardFilterOption;
}

export const readDemographicsBarChart = ({
  chartsFilters,
  globalFilters,
  signal,
}: {
  chartsFilters?: {
    metric?: DashboardFilterOption;
    product_level_three?: DashboardFilterOption;
    store_level_two?: DashboardFilterOption;
  };
  globalFilters?: DemographicsGlobalFilters;
  signal?: AbortSignal;
}) => {
  return axios<DashboardChartResponse>({
    method: 'get',
    params: {
      demographic_attribute: globalFilters?.demographic_attribute?.value,
      metric: chartsFilters?.metric?.value,
      product_level_one: globalFilters?.product_level_one?.value,
      product_level_three: chartsFilters?.product_level_three?.value,
      store_level_two: chartsFilters?.store_level_two?.value,
      time_int: globalFilters?.time_int?.value,
    },
    signal,
    url: '/v2/api/dashboards/demographics-tab/bar-chart',
    withCredentials: true,
  });
};

export const readDemographicsChartsFiltersData = ({
  globalFilters,
  signal,
}: {
  globalFilters?: DemographicsGlobalFilters;
  signal?: AbortSignal;
}) => {
  return axios<DashboardFiltersResponse>({
    method: 'get',
    params: {
      demographic_attribute: globalFilters?.demographic_attribute?.value,
      product_level_one: globalFilters?.product_level_one?.value,
      time_int: globalFilters?.time_int?.value,
    },
    signal,
    transformResponse: [
      (data: string) => transformFilters(JSON.parse(data) as DashboardFiltersPayload),
    ],
    url: '/v2/api/dashboards/demographics-tab/charts/filters',
    withCredentials: true,
  });
};

export const readDemographicsGlobalFiltersData = ({ signal }: { signal?: AbortSignal }) => {
  return axios<DashboardFiltersResponse>({
    method: 'get',
    signal,
    transformResponse: [
      (data: string) => transformFilters(JSON.parse(data) as DashboardFiltersPayload),
    ],
    url: '/v2/api/dashboards/demographics-tab/global-filters',
    withCredentials: true,
  });
};

export const readStoresBarChart = ({
  chartsFilters,
  globalFilters,
  signal,
}: ReadStoresChartOptions) => {
  return axios<DashboardChartResponse>({
    method: 'get',
    params: {
      metric: chartsFilters?.metric?.value,
      product_level_one: globalFilters?.product_level_one?.value,
      product_level_three: chartsFilters?.product_level_three
        ?.map((filter) => filter.value)
        .join(';'),
      store_level_one: chartsFilters?.store_level_one?.value,
      store_level_three: chartsFilters?.store_level_three?.value,
      store_level_two: chartsFilters?.store_level_two?.value,
      time_int: globalFilters?.time_int?.value,
    },
    signal,
    url: '/v2/api/dashboards/stores-tab/bar-chart',
    withCredentials: true,
  });
};

export const readStoresChartsFiltersData = ({
  chartsFilters,
  globalFilters,
  signal,
}: {
  chartsFilters?: StoresChartsFilters;
  globalFilters?: StoresGlobalFilters;
  signal?: AbortSignal;
}) => {
  return axios<DashboardFiltersResponse>({
    method: 'get',
    params: {
      product_level_one: globalFilters?.product_level_one?.value,
      store_level_one: chartsFilters?.store_level_one?.value,
      store_level_three: chartsFilters?.store_level_three?.value,
      store_level_two: chartsFilters?.store_level_two?.value,
      time_int: globalFilters?.time_int?.value,
    },
    signal,
    transformResponse: [
      (data: string) => transformFilters(JSON.parse(data) as DashboardFiltersPayload),
    ],
    url: '/v2/api/dashboards/stores-tab/charts/filters',
    withCredentials: true,
  });
};

export const readStoresGlobalFiltersData = ({ signal }: { signal?: AbortSignal }) => {
  return axios<DashboardFiltersResponse>({
    method: 'get',
    signal,
    transformResponse: [
      (data: string) => transformFilters(JSON.parse(data) as DashboardFiltersPayload),
    ],
    url: '/v2/api/dashboards/stores-tab/global-filters',
    withCredentials: true,
  });
};

export const readStoresLineChart = ({
  chartsFilters,
  globalFilters,
  signal,
}: ReadStoresChartOptions) => {
  return axios<DashboardChartResponse>({
    method: 'get',
    params: {
      metric: chartsFilters?.metric?.value,
      product_level_one: globalFilters?.product_level_one?.value,
      product_level_three: chartsFilters?.product_level_three
        ?.map((filter) => filter.value)
        .join(';'),
      store_level_one: chartsFilters?.store_level_one?.value,
      store_level_three: chartsFilters?.store_level_three?.value,
      store_level_two: chartsFilters?.store_level_two?.value,
      time_int: globalFilters?.time_int?.value,
    },
    signal,
    url: '/v2/api/dashboards/stores-tab/line-graph',
    withCredentials: true,
  });
};

export const readStoresTable = ({
  globalFilters,
  signal,
  tableFilters,
}: {
  globalFilters?: StoresGlobalFilters;
  signal?: AbortSignal;
  tableFilters?: {
    metric_type?: DashboardFilterOption;
    product_level_three?: DashboardFilterOption;
    store_level_one?: DashboardFilterOption;
  };
}) => {
  return axios<DashboardTableResponse>({
    method: 'get',
    params: {
      metric_type: tableFilters?.metric_type?.value,
      product_level_one: globalFilters?.product_level_one?.value,
      product_level_three: tableFilters?.product_level_three?.value,
      store_level_one: tableFilters?.store_level_one?.value,
      time_int: globalFilters?.time_int?.value,
    },
    signal,
    transformResponse: [
      (data: string) => transformTableData(JSON.parse(data) as DashboardTablePayload),
    ],
    url: '/v2/api/dashboards/stores-tab/table',
    withCredentials: true,
  });
};

export const readStoresTableFiltersData = ({
  globalFilters,
  signal,
}: {
  globalFilters?: StoresGlobalFilters;
  signal?: AbortSignal;
}) => {
  return axios<DashboardFiltersResponse>({
    method: 'get',
    params: {
      product_level_one: globalFilters?.product_level_one?.value,
      time_int: globalFilters?.time_int?.value,
    },
    signal,
    transformResponse: [
      (data: string) => transformFilters(JSON.parse(data) as DashboardFiltersPayload),
    ],
    url: '/v2/api/dashboards/stores-tab/table/filters',
    withCredentials: true,
  });
};

export const readSummaryBarChart = ({
  chartsFilters,
  globalFilters,
  signal,
}: ReadSummaryChartOptions) => {
  return axios<DashboardChartResponse>({
    method: 'get',
    params: {
      breakout: globalFilters?.breakout?.value,
      metric: chartsFilters?.metric?.value,
      product: chartsFilters?.product?.map((filter) => filter.value).join(';'),
      product_level_one: globalFilters?.product_level_one?.value,
      time_int: globalFilters?.time_int?.value,
    },
    signal,
    url: '/v2/api/dashboards/summary-tab/bar-chart',
    withCredentials: true,
  });
};

export const readSummaryLineChart = ({
  chartsFilters,
  globalFilters,
  signal,
}: ReadSummaryChartOptions) => {
  return axios<DashboardChartResponse>({
    method: 'get',
    params: {
      breakout: globalFilters?.breakout?.value,
      metric: chartsFilters?.metric?.value,
      product: chartsFilters?.product?.map((filter) => filter.value).join(';'),
      product_level_one: globalFilters?.product_level_one?.value,
      time_int: globalFilters?.time_int?.value,
    },
    signal,
    url: '/v2/api/dashboards/summary-tab/line-graph',
    withCredentials: true,
  });
};

export const readSummaryChartsFiltersData = ({
  globalFilters,
  signal,
}: ReadSummaryFiltersOptions) => {
  return axios<DashboardFiltersResponse>({
    method: 'get',
    params: {
      breakout: globalFilters?.breakout?.value,
      product_level_one: globalFilters?.product_level_one?.value,
      time_int: globalFilters?.time_int?.value,
    },
    signal,
    transformResponse: [
      (data: string) => transformFilters(JSON.parse(data) as DashboardFiltersPayload),
    ],
    url: '/v2/api/dashboards/summary-tab/charts/filters',
    withCredentials: true,
  });
};

export const readSummaryGlobalFiltersData = ({ signal }: { signal?: AbortSignal }) => {
  return axios<DashboardFiltersResponse>({
    method: 'get',
    signal,
    transformResponse: [
      (data: string) => transformFilters(JSON.parse(data) as DashboardFiltersPayload),
    ],
    url: '/v2/api/dashboards/summary-tab/global-filters',
    withCredentials: true,
  });
};

export const readSummaryTable = ({
  globalFilters,
  signal,
  tableFilters,
}: {
  globalFilters?: SummaryGlobalFilters;
  signal?: AbortSignal;
  tableFilters?: {
    metric_type?: DashboardFilterOption;
    store_level_one?: DashboardFilterOption;
  };
}) => {
  return axios<DashboardTableResponse>({
    method: 'get',
    params: {
      breakout: globalFilters?.breakout?.value,
      metric_type: tableFilters?.metric_type?.value,
      product_level_one: globalFilters?.product_level_one?.value,
      store_level_one: tableFilters?.store_level_one?.value,
      time_int: globalFilters?.time_int?.value,
    },
    signal,
    transformResponse: [
      (data: string) => transformTableData(JSON.parse(data) as DashboardTablePayload),
    ],
    url: '/v2/api/dashboards/summary-tab/table',
    withCredentials: true,
  });
};

export const readSummaryTableFiltersData = ({
  globalFilters,
  signal,
}: ReadSummaryFiltersOptions) => {
  return axios<DashboardFiltersResponse>({
    method: 'get',
    params: {
      breakout: globalFilters?.breakout?.value,
      product_level_one: globalFilters?.product_level_one?.value,
      time_int: globalFilters?.time_int?.value,
    },
    signal,
    transformResponse: [
      (data: string) => transformFilters(JSON.parse(data) as DashboardFiltersPayload),
    ],
    url: '/v2/api/dashboards/summary-tab/table/filters',
    withCredentials: true,
  });
};
